(() => {
  const ROOT_SELECTOR = '.pv-premium-product';

  class PurrVibesProductEnhancer {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.stickyBar = root.querySelector('[data-pv-sticky-atc]');
      this.stickyButton = root.querySelector('[data-pv-sticky-button]');
      this.observedButton = null;
      this.syncFrame = null;

      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.queueSync = this.queueSync.bind(this);
    }

    init() {
      if (this.root.dataset.pvEnhanced === 'true') return;

      this.root.dataset.pvEnhanced = 'true';
      this.root.addEventListener('change', this.queueSync);

      if (this.stickyBar && this.stickyButton) {
        this.stickyBar.hidden = false;
        this.stickyButton.addEventListener('click', this.handleStickyClick);
        document.body.classList.add('pv-sticky-atc-enabled');
      }

      this.mutationObserver = new MutationObserver(this.queueSync);
      this.mutationObserver.observe(this.root, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.sync();
    }

    get primaryButton() {
      return this.root.querySelector('[data-pv-buy-zone] .product-form__submit');
    }

    get variantPicker() {
      return this.root.querySelector('.pv-variant-picker');
    }

    getPriceText() {
      const priceContainer = this.root.querySelector(`#price-${this.sectionId}`);
      if (!priceContainer) return '';

      const priceRoot = priceContainer.querySelector('.price');
      const isOnSale = priceRoot?.classList.contains('price--on-sale');
      const selector = isOnSale
        ? '.price__sale .price-item--sale'
        : '.price__regular .price-item--regular';
      const price = priceContainer.querySelector(selector);

      return price?.textContent.trim().replace(/\s+/g, ' ') || '';
    }

    syncPrimaryLabel(button, priceText) {
      if (!button || this.root.dataset.pvPriceInAtc !== 'true') return;

      const label = button.querySelector('span');
      if (!label || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const visibleLabel = label.textContent.trim().replace(/\s+/g, ' ');

      if (!label.dataset.pvBaseLabel) {
        label.dataset.pvBaseLabel = visibleLabel.split(' — ')[0];
      }

      const expectedLabel = priceText
        ? `${label.dataset.pvBaseLabel} — ${priceText}`
        : label.dataset.pvBaseLabel;

      if (visibleLabel !== expectedLabel) {
        label.textContent = expectedLabel;
      }
    }

    syncSticky(button, priceText) {
      if (!this.stickyBar || !this.stickyButton) return;

      const stickyPrice = this.stickyBar.querySelector('[data-pv-sticky-price]');
      const stickyLabel = this.stickyBar.querySelector('[data-pv-sticky-label]');
      const primaryLabel = button?.querySelector('span')?.textContent.trim();
      const isDisabled =
        !button ||
        button.disabled ||
        button.getAttribute('aria-disabled') === 'true';

      if (stickyPrice && stickyPrice.textContent.trim() !== priceText) {
        stickyPrice.textContent = priceText;
      }

      this.stickyButton.disabled = isDisabled;

      if (stickyLabel && isDisabled && primaryLabel) {
        stickyLabel.textContent = primaryLabel.split(' — ')[0];
      } else if (stickyLabel && stickyLabel.dataset.defaultLabel) {
        stickyLabel.textContent = stickyLabel.dataset.defaultLabel;
      }

      this.observePrimaryButton(button);
    }

    observePrimaryButton(button) {
      if (!this.stickyBar || button === this.observedButton) return;

      this.buttonObserver?.disconnect();
      this.observedButton = button;

      if (!button) {
        this.stickyBar.classList.remove('is-visible');
        return;
      }

      this.buttonObserver = new IntersectionObserver(
        ([entry]) => {
          this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        },
        {
          threshold: 0.15,
        }
      );

      this.buttonObserver.observe(button);
    }

    handleStickyClick() {
      const button = this.primaryButton;

      if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') {
        button.click();
        return;
      }

      this.variantPicker?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'center',
      });
    }

    queueSync() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => this.sync());
    }

    sync() {
      this.syncFrame = null;
      const button = this.primaryButton;
      const priceText = this.getPriceText();

      this.syncPrimaryLabel(button, priceText);
      this.syncSticky(button, priceText);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      new PurrVibesProductEnhancer(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
